'use client';

import { usePathname } from 'next/navigation';
import { isSensitiveReplayPath } from '@/lib/analytics-privacy.mjs';

/**
 * Route-gated Umami session replay loader.
 *
 * The recorder is a separate script from the pageview tracker and has no
 * built-in route exclusion, so the gate lives here: on sensitive routes
 * (`/inquiries/*` token pages, `/admin/*` internal tooling) this renders
 * nothing and no recording can start. Global kill-switch via
 * `NEXT_PUBLIC_UMAMI_REPLAY_ENABLED=false` is evaluated in the root
 * layout, which simply omits this component.
 *
 * Note: this gates recorder *startup* (full page loads, including email
 * token links). For client-side navigation into a sensitive route while
 * the recorder is already running, content is protected by the
 * `.umami-block` subtree (see AppShellWrapper) paired with the Umami
 * dashboard Block Selector setting.
 */
export default function UmamiReplay({ umamiUrl, websiteId }) {
    const pathname = usePathname();

    if (!umamiUrl || !websiteId) return null;
    if (isSensitiveReplayPath(pathname)) return null;

    return (
        <script
            defer
            src={`${umamiUrl}/recorder.js`}
            data-website-id={websiteId}
            data-sample-rate="0.15"
            data-mask-level="moderate"
            data-max-duration="300000"
        />
    );
}
